// src/pages/admin/AdminMedicineList.tsx
import React, { useEffect, useState } from 'react';
import api from '../../api/axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { translateMedicineName } from '../../utils/medTranslations';

interface PricePlan {
  id: number;
  plan_type: 'single_purchase' | 'subscription';
  duration_months: number;              // 订阅月数；单品时通常为 0
  price_tax_excluded: number;
  price_tax_included: number;
  // 数量/覆盖（可选）
  quantity_units?: number | null;       // 例：30（錠）
  coverage_months_min?: number | null;  // 例：1
  coverage_months_max?: number | null;  // 例：2
  billing_unit?: 'month' | 'quantity';  // 计费单位
}

interface Medicine {
  id: number;
  name: string;
  type: string;
  note?: string;
  service_type: string;
  price_plans: PricePlan[];
}

const GROUP_PAGE_SIZE = 10;

const AdminMedicineList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [medicines, setMedicines] = useState<Medicine[]>([]);
  const [editing, setEditing] = useState<Medicine | null>(null);
  const [taxRate, setTaxRate] = useState(10);
  // 编辑弹窗内：税抜 / 税込输入模式
  const [priceMode, setPriceMode] = useState<'ex' | 'in'>('ex');

  // 每个 service_type 的折叠状态
  const [collapsedGroups, setCollapsedGroups] = useState<{ [key: string]: boolean }>({});
  // 每个 service_type 的当前页
  const [groupPages, setGroupPages] = useState<{ [key: string]: number }>({});

  const serviceOptions = [
    'shipping',
    'aga_male',
    'aga_female',
    'obesity_male',
    'obesity_female',
    'ed_premature',
    'pill',
  ];

  // ===== 税计算工具 =====
  const calcIncFromEx = (ex: number) => {
    if (!Number.isFinite(ex)) return 0;
    const k = 1 + (Number.isFinite(taxRate) ? taxRate : 0) / 100;
    return Math.round(ex * k);
  };

  const calcExFromInc = (inc: number) => {
    if (!Number.isFinite(inc)) return 0;
    const k = 1 + (Number.isFinite(taxRate) ? taxRate : 0) / 100;
    if (k <= 0) return 0;
    return Math.round(inc / k);
  };

  const fetchMedicines = async () => {
    try {
      // 一次取足够多的数据，后面在前端按服务种别分页
      const res = await api.get('/admin/medicines', {
        params: { skip: 0, limit: 1000 },
      });
      const data = res.data as Medicine[];
      setMedicines(data);

      // 初始化每个 service_type 的页码为 1
      const initialPages: { [key: string]: number } = {};
      for (const med of data) {
        if (!initialPages[med.service_type]) {
          initialPages[med.service_type] = 1;
        }
      }
      setGroupPages(initialPages);
    } catch (err) {
      console.error('Failed to fetch medicines:', err);
    }
  };

  useEffect(() => {
    fetchMedicines();
  }, []);

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('admin.confirm_delete') || '削除しますか？')) return;
    try {
      await api.delete(`/admin/medicines/${id}`);
      fetchMedicines();
    } catch (err) {
      console.error('削除失敗:', err);
      alert(t('admin.delete_failed') || '削除に失敗しました');
    }
  };

  /** ✅ 和 DoctorRecord 对齐的“プラン名”显示 */
  const renderPlanLabel = (plan: PricePlan, serviceType?: string) => {
    // 配送料：特殊显示
    if (serviceType === 'shipping') {
      return (t('admin.shipping') as string) || '配送料';
    }

    // 订阅：优先看 plan_type + duration_months
    if (plan.plan_type === 'subscription' && (plan.duration_months || 0) > 0) {
      if (plan.duration_months === 1) {
        return (t('admin.medicine.subscription_1m') as string) || '定期 1ヶ月';
      }
      return (
        (t('admin.medicine.subscription_n_months', { n: plan.duration_months }) as string) ||
        `定期 ${plan.duration_months}ヶ月`
      );
    }

    // 其它情况，一律当作「単品購入」
    return (t('admin.medicine.single_purchase') as string) || '単品購入';
  };

  const handleEditChange = (field: keyof Medicine, value: string) => {
    if (!editing) return;
    setEditing({ ...editing, [field]: value } as Medicine);
  };

  // 保存编辑
  const handleSaveEdit = async () => {
    if (!editing) return;
    try {
      const payload = {
        name: editing.name,
        type: editing.type,
        note: editing.note,
        service_type: editing.service_type,
        price_plans: editing.price_plans,
      };

      if (editing.id === 0) {
        // 新規作成（配送料の追加）
        await api.post('/admin/medicines', payload);
      } else {
        // 既存の編集
        await api.put(`/admin/medicines/${editing.id}`, payload);
      }

      setEditing(null);
      fetchMedicines();
    } catch (err) {
      console.error('保存失敗:', err);
      alert(t('admin.update_failed') || '保存に失敗しました');
    }
  };

  // 分组
  const groupedMedicines = medicines.reduce((acc, med) => {
    if (!acc[med.service_type]) acc[med.service_type] = [];
    acc[med.service_type].push(med);
    return acc;
  }, {} as { [key: string]: Medicine[] });

  // 排序：不包含 shipping
  const sortedServiceTypes = Object.keys(groupedMedicines)
    .filter(type => type !== 'shipping')
    .sort((a, b) => {
      return serviceOptions.indexOf(a) - serviceOptions.indexOf(b);
    });

  const shippingGroup = groupedMedicines['shipping'] || [];

  // 修改某个 serviceType 的页码
  const changeGroupPage = (serviceType: string, delta: number) => {
    setGroupPages((prev) => {
      const current = prev[serviceType] || 1;
      const total = Math.max(
        1,
        Math.ceil((groupedMedicines[serviceType]?.length || 0) / GROUP_PAGE_SIZE),
      );
      const next = Math.min(total, Math.max(1, current + delta));
      return { ...prev, [serviceType]: next };
    });
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">
          {t('admin.medicine_list') || '薬品一覧'}
        </h2>
        <button
          onClick={() => navigate('/admin/medicines')}
          className="text-sm px-4 py-2 rounded bg-white hover:bg-gray-100 border text-gray-700"
        >
          ← {t('admin.common.back') || '戻る'}
        </button>
      </div>

      {/* === 配送料専用カード (Shipping Fee Card) === */}
      <div className="mb-6 border rounded-lg bg-coral/5 border-coral/20">
        <div className="px-4 py-3 bg-coral-light rounded-t-lg font-bold flex justify-between items-center text-navy">
          <span>{t('admin.shipping') || '配送料'}</span>
          <div className="flex items-center gap-3">
            <span className="text-xs font-normal bg-white px-2 py-1 rounded text-coral border border-coral/20">
              {t('admin.shipping_desc') || '一般配送・コールドチェーン等の設定'}
            </span>
            <button
              onClick={() => {
                // Create a new shipping medicine template
                setEditing({
                  id: 0, // 0 means new item
                  name: '',
                  type: 'single',
                  service_type: 'shipping',
                  note: '',
                  price_plans: [
                    {
                      id: 0,
                      plan_type: 'single_purchase',
                      duration_months: 0,
                      price_tax_excluded: 0,
                      price_tax_included: 0,
                      quantity_units: null,
                      coverage_months_min: null,
                      coverage_months_max: null,
                    }
                  ]
                });
              }}
              className="px-3 py-1 bg-gradient-primary text-white rounded-full shadow-card transition-transform hover:-translate-y-0.5 whitespace-nowrap text-sm"
            >
              + {t('admin.add_shipping') || '配送料を追加'}
            </button>
          </div>
        </div>
        <div className="p-0">
          {shippingGroup.length === 0 ? (
            <div className="p-4 text-gray-500 text-sm">
              {t('admin.common.no_data') || 'データがありません'}
            </div>
          ) : (
            shippingGroup.map((med) => (
              <div key={med.id} className="bg-white border-b last:border-b-0 px-4 py-3">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="text-lg font-semibold">{translateMedicineName(med.name, i18n.language)}</h3>
                    {med.note && (
                      <p className="text-sm mt-1 text-gray-700">
                        {t('admin.medicine.note') || 'メモ'}: {med.note}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditing(med)}
                      className="px-3 py-1 rounded border text-coral hover:bg-coral/5"
                    >
                      {t('admin.common.edit') || '編集'}
                    </button>
                    <button
                      onClick={() => handleDelete(med.id)}
                      className="px-3 py-1 rounded border text-red-600 hover:bg-red-50"
                    >
                      {t('admin.common.delete') || '削除'}
                    </button>
                  </div>
                </div>
                {/* 价格方案列表 */}
                <div className="overflow-x-auto">
                  <table className="w-full table-auto text-sm border">
                    <thead>
                      <tr className="bg-gray-100 text-left">
                        <th className="px-2 py-1">{t('admin.medicine.plan') || 'プラン'}</th>
                        <th className="px-2 py-1">{t('admin.medicine.price_ex') || '税抜き'}</th>
                        <th className="px-2 py-1">{t('admin.medicine.price_in') || '税込み'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {med.price_plans.map((plan) => (
                        <tr key={plan.id} className="border-t">
                          <td className="px-2 py-1">{renderPlanLabel(plan, med.service_type)}</td>
                          <td className="px-2 py-1">{plan.price_tax_excluded.toLocaleString()} 円</td>
                          <td className="px-2 py-1">{plan.price_tax_included.toLocaleString()} 円</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 按 service_type 分组显示，每个卡片自己分页 (Exclude shipping) */}
      {sortedServiceTypes.map((serviceType) => {
        const group = groupedMedicines[serviceType] || [];
        const page = groupPages[serviceType] || 1;
        const totalPages = Math.max(1, Math.ceil(group.length / GROUP_PAGE_SIZE));
        const start = (page - 1) * GROUP_PAGE_SIZE;
        const end = start + GROUP_PAGE_SIZE;
        const pagedMeds = group.slice(start, end);

        const isCollapsed = collapsedGroups[serviceType];

        return (
          <div key={serviceType} className="mb-4 border rounded-lg bg-gray-50">
            <button
              className="w-full flex items-center justify-between px-4 py-2 font-semibold bg-gray-100 hover:bg-gray-200 rounded-t-lg"
              onClick={() =>
                setCollapsedGroups((prev) => ({
                  ...prev,
                  [serviceType]: !prev[serviceType],
                }))
              }
            >
              <span>
                {t('admin.medicine.service_type_label', '服务类型')}:{' '}
                {t(`service.${serviceType}`) || serviceType}
              </span>
              <svg
                className={`w-4 h-4 transform transition-transform duration-200 ${isCollapsed ? 'rotate-0' : 'rotate-90'
                  }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {!isCollapsed && (
              <>
                {pagedMeds.map((med) => (
                  <div key={med.id} className="bg-white border-t px-4 py-3">
                    {/* 头部：药名 + 操作按钮 */}
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="text-lg font-semibold">{translateMedicineName(med.name, i18n.language)}</h3>
                        <p className="text-sm text-gray-600">
                          {t(`admin.medicine.type.${med.type}`) || med.type}
                        </p>
                        {med.note && (
                          <p className="text-sm mt-1 text-gray-700">
                            {t('admin.medicine.note') || 'メモ'}: {med.note}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditing(med)}
                          className="px-3 py-1 rounded border text-coral hover:bg-coral/5"
                        >
                          {t('admin.common.edit') || '編集'}
                        </button>
                        <button
                          onClick={() => handleDelete(med.id)}
                          className="px-3 py-1 rounded border text-red-600 hover:bg-red-50"
                        >
                          {t('admin.common.delete') || '削除'}
                        </button>
                      </div>
                    </div>

                    {/* 价格方案列表 */}
                    <div className="overflow-x-auto">
                      {/* ▼ モバイル: プランをカード表示（横スクロールを出さず縦積み） */}
                      <div className="md:hidden divide-y">
                        {med.price_plans.map((plan) => {
                          const qty = plan.quantity_units ?? null;
                          const covMin = plan.coverage_months_min ?? null;
                          const covMax = plan.coverage_months_max ?? null;

                          // テーブルと同一の数量・カバー表示ロジック
                          let quantityText = '-';
                          let coverageText = '-';

                          if (
                            plan.plan_type === 'subscription' &&
                            (plan.duration_months || 0) > 0
                          ) {
                            quantityText = '-';
                            coverageText = `${plan.duration_months}`;
                          } else if (qty && qty > 0) {
                            quantityText = `${qty}`;
                            if (covMin && covMax) {
                              coverageText = `${covMin}〜${covMax}`;
                            } else if (covMin) {
                              coverageText = `${covMin}+`;
                            } else if (covMax) {
                              coverageText = `〜${covMax}`;
                            } else {
                              coverageText = '-';
                            }
                          }

                          return (
                            <div
                              key={plan.id}
                              className="py-2 first:pt-0 last:pb-0 min-w-0"
                            >
                              {/* プラン名 */}
                              <div className="font-medium text-sm break-words">
                                {renderPlanLabel(plan, med.service_type)}
                              </div>
                              {/* 各列を「ラベル：値」で縦積み表示 */}
                              <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-700">
                                <div className="min-w-0 break-words">
                                  <span className="text-gray-400">
                                    {t('admin.medicine.price_ex') || '税抜き'}：
                                  </span>
                                  {plan.price_tax_excluded.toLocaleString()} 円
                                </div>
                                <div className="min-w-0 break-words">
                                  <span className="text-gray-400">
                                    {t('admin.medicine.price_in') || '税込み'}：
                                  </span>
                                  {plan.price_tax_included.toLocaleString()} 円
                                </div>
                                <div className="min-w-0 break-words">
                                  <span className="text-gray-400">
                                    {t('admin.medicine.quantity_units') || '数量（本）'}：
                                  </span>
                                  {quantityText}
                                </div>
                                <div className="min-w-0 break-words">
                                  <span className="text-gray-400">
                                    {t('admin.medicine.coverage_range') || 'カバー（月）'}：
                                  </span>
                                  {coverageText}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* ▼ デスクトップ: テーブル表示 */}
                      <table className="w-full table-auto text-sm border hidden md:table">
                        <thead>
                          <tr className="bg-gray-100 text-left">
                            <th className="px-2 py-1">
                              {t('admin.medicine.plan') || 'プラン'}
                            </th>
                            <th className="px-2 py-1">
                              {t('admin.medicine.price_ex') || '税抜き'}
                            </th>
                            <th className="px-2 py-1">
                              {t('admin.medicine.price_in') || '税込み'}
                            </th>
                            <th className="px-2 py-1">
                              {t('admin.medicine.quantity_units') ||
                                '数量（本）'}
                            </th>
                            <th className="px-2 py-1">
                              {t('admin.medicine.coverage_range') ||
                                'カバー（月）'}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {med.price_plans.map((plan) => {
                            const qty = plan.quantity_units ?? null;
                            const covMin = plan.coverage_months_min ?? null;
                            const covMax = plan.coverage_months_max ?? null;

                            let quantityText = '-';
                            let coverageText = '-';

                            if (
                              plan.plan_type === 'subscription' &&
                              (plan.duration_months || 0) > 0
                            ) {
                              quantityText = '-';
                              coverageText = `${plan.duration_months}`;
                            } else if (qty && qty > 0) {
                              quantityText = `${qty}`;
                              if (covMin && covMax) {
                                coverageText = `${covMin}〜${covMax}`;
                              } else if (covMin) {
                                coverageText = `${covMin}+`;
                              } else if (covMax) {
                                coverageText = `〜${covMax}`;
                              } else {
                                coverageText = '-';
                              }
                            }

                            return (
                              <tr key={plan.id} className="border-t">
                                <td className="px-2 py-1">
                                  {renderPlanLabel(plan, med.service_type)}
                                </td>
                                <td className="px-2 py-1">
                                  {plan.price_tax_excluded.toLocaleString()} 円
                                </td>
                                <td className="px-2 py-1">
                                  {plan.price_tax_included.toLocaleString()} 円
                                </td>
                                <td className="px-2 py-1">{quantityText}</td>
                                <td className="px-2 py-1">{coverageText}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}

                {/* serviceType 内分页控件（超过 10 条才显示） */}
                {group.length > GROUP_PAGE_SIZE && (
                  <div className="flex justify-end items-center gap-2 px-4 py-2 border-t bg-gray-50">
                    <button
                      disabled={page === 1}
                      onClick={() => changeGroupPage(serviceType, -1)}
                      className="px-3 py-1 border rounded bg-white hover:bg-gray-100 disabled:opacity-50"
                    >
                      ← {t('admin.common.prev') || '前へ'}
                    </button>
                    <span className="text-sm">
                      {t('admin.common.page') || 'ページ'} {page} / {totalPages}
                    </span>
                    <button
                      disabled={page >= totalPages}
                      onClick={() => changeGroupPage(serviceType, 1)}
                      className="px-3 py-1 border rounded bg-white hover:bg-gray-100 disabled:opacity-50"
                    >
                      {t('admin.common.next') || '次へ'} →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      {/* 编辑弹窗 */}
      {editing && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4"
          onClick={() => setEditing(null)}
        >
          <div
            className="bg-white rounded-lg shadow p-4 w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-4">
              {t('admin.edit_medicine') || '薬品の編集'}
            </h3>

            <input
              type="text"
              className="border w-full mb-2 px-3 py-1 rounded"
              value={editing.name}
              onChange={(e) => handleEditChange('name', e.target.value)}
            />
            <select
              value={editing.type}
              onChange={(e) => handleEditChange('type', e.target.value)}
              className="border w-full mb-2 px-3 py-1 rounded"
            >
              <option value="single">
                {t('admin.medicine.type.single') || '単品'}
              </option>
              <option value="set">
                {t('admin.medicine.type.set') || 'セット'}
              </option>
            </select>

            {editing.service_type === 'shipping' ? (
              <div className="mb-2">
                <label className="text-sm text-gray-600 block mb-1">
                  {t('admin.service_type') || 'サービス'}
                </label>
                <input
                  type="text"
                  value={t('admin.shipping') || '配送料'}
                  disabled
                  className="border w-full px-3 py-1 rounded bg-gray-100 text-gray-500"
                />
              </div>
            ) : (
              <select
                value={editing.service_type}
                onChange={(e) => handleEditChange('service_type', e.target.value)}
                className="border w-full mb-2 px-3 py-1 rounded"
              >
                <option value="">{t('admin.select_service') || 'サービスを選択'}</option>
                {serviceOptions.map((key) => (
                  <option key={key} value={key}>
                    {t(`service.${key}`) || key}
                  </option>
                ))}
              </select>
            )}

            <textarea
              className="border w-full mb-2 px-3 py-1 rounded"
              value={editing.note || ''}
              onChange={(e) => handleEditChange('note', e.target.value)}
            />

            {/* 税率 & 输入模式切换 */}
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">
                  {t('admin.medicine.tax_rate') || '税率'} (%)
                </label>
                <input
                  type="number"
                  value={taxRate}
                  onChange={(e) => setTaxRate(Number(e.target.value) || 0)}
                  className="border w-20 px-2 py-1 rounded"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {t('admin.medicine.input_mode') || '入力モード'}:
                </span>
                <button
                  type="button"
                  onClick={() => setPriceMode('ex')}
                  className={`px-3 py-1 rounded-full text-xs border ${priceMode === 'ex'
                    ? 'bg-coral/5 border-coral/40 text-coral'
                    : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                >
                  {t('admin.medicine.mode_ex') || '税抜を入力'}
                </button>
                <button
                  type="button"
                  onClick={() => setPriceMode('in')}
                  className={`px-3 py-1 rounded-full text-xs border ${priceMode === 'in'
                    ? 'bg-coral/5 border-coral/40 text-coral'
                    : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                >
                  {t('admin.medicine.mode_in') || '税込を入力'}
                </button>
              </div>
            </div>

            {/* plan 编辑（根据 priceMode 输入税抜 or 税込，另一侧自动计算） */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {editing.price_plans.map((plan) => {
                const qty = plan.quantity_units ?? null;
                const covMin = plan.coverage_months_min ?? null;
                const covMax = plan.coverage_months_max ?? null;

                // 与上面表格一样的覆盖显示逻辑
                let quantityText = '-';
                let coverageText = '-';

                if (
                  plan.plan_type === 'subscription' &&
                  (plan.duration_months || 0) > 0
                ) {
                  quantityText = '-';
                  coverageText = `${plan.duration_months}${(t('admin.medicine.unit_month') as string) || 'ヶ月'
                    }`;
                } else if (qty && qty > 0) {
                  quantityText = `${qty}${(t('admin.medicine.unit_piece') as string) || '本'
                    }`;
                  if (covMin && covMax) {
                    coverageText = `${covMin}〜${covMax}${(t('admin.medicine.unit_month') as string) || 'ヶ月'
                      }`;
                  } else if (covMin) {
                    coverageText = `${covMin}+${(t('admin.medicine.unit_month') as string) || 'ヶ月'
                      }`;
                  } else if (covMax) {
                    coverageText = `〜${covMax}${(t('admin.medicine.unit_month') as string) || 'ヶ月'
                      }`;
                  } else {
                    coverageText = '-';
                  }
                }

                const editableValue =
                  priceMode === 'ex'
                    ? plan.price_tax_excluded
                    : plan.price_tax_included;

                const counterpartValue =
                  priceMode === 'ex'
                    ? calcIncFromEx(plan.price_tax_excluded)
                    : calcExFromInc(plan.price_tax_included);

                const handlePlanPriceChange = (value: string) => {
                  const num = Number(value || '0');
                  setEditing((prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      price_plans: prev.price_plans.map((p) => {
                        if (p.id !== plan.id) return p;
                        if (priceMode === 'ex') {
                          const ex = num;
                          const inc = calcIncFromEx(ex);
                          return {
                            ...p,
                            price_tax_excluded: ex,
                            price_tax_included: inc,
                          };
                        } else {
                          const inc = num;
                          const ex = calcExFromInc(inc);
                          return {
                            ...p,
                            price_tax_excluded: ex,
                            price_tax_included: inc,
                          };
                        }
                      }),
                    };
                  });
                };

                return (
                  <div key={plan.id} className="border rounded p-3">
                    <p className="text-sm font-medium mb-2">
                      {renderPlanLabel(plan, editing?.service_type)}
                    </p>

                    <p className="text-xs text-gray-600 mb-2">
                      {(t('admin.medicine.quantity_units') as string) ||
                        '数量'}
                      : {quantityText}{' '}
                      {(t('admin.medicine.coverage_range') as string) ||
                        'カバー'}
                      : {coverageText}
                    </p>

                    <label className="block text-xs text-gray-600 mb-1">
                      {priceMode === 'ex'
                        ? t('admin.medicine.price_ex') || '税抜き価格'
                        : t('admin.medicine.price_in') || '税込み価格'}
                    </label>
                    <input
                      type="number"
                      className="border px-2 py-1 rounded w-full"
                      value={editableValue}
                      onChange={(e) => handlePlanPriceChange(e.target.value)}
                    />
                    <p className="text-sm text-gray-600 mt-1">
                      {priceMode === 'ex'
                        ? (t('admin.medicine.price_in') || '税込み')
                        : (t('admin.medicine.price_ex') || '税抜き')}
                      : {counterpartValue.toLocaleString()} 円
                    </p>

                    {/* Billing Unit Selector */}
                    <div className="mt-3">
                      <label className="block text-xs text-gray-600 mb-1">
                        计费单位:
                      </label>
                      <select
                        value={plan.billing_unit || 'quantity'}
                        onChange={(e) => {
                          const newUnit = e.target.value as 'month' | 'quantity';
                          setEditing(prev => {
                            if (!prev) return prev;
                            return {
                              ...prev,
                              price_plans: prev.price_plans.map(p =>
                                p.id === plan.id ? { ...p, billing_unit: newUnit } : p
                              )
                            };
                          });
                        }}
                        className="border px-2 py-1 rounded w-full text-sm"
                      >
                        <option value="quantity">按数量 (錠/個)</option>
                        <option value="month">按月 (ヶ月)</option>
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-1 border rounded hover:bg-gray-100"
              >
                {t('admin.common.cancel') || 'キャンセル'}
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-1 bg-gradient-primary text-white rounded-full shadow-card transition-transform hover:-translate-y-0.5 whitespace-nowrap"
              >
                {t('admin.common.save') || '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMedicineList;
